(() => {
  if (window.gpPlanterAsyncFilters) return;
  window.gpPlanterAsyncFilters = true;
  const states = new WeakMap();
  const key = url => url.pathname + url.search;
  const typeLinks = '.gp-pd-nav a, .gp-pd-all, .gp-pd-browse > a, .gp-pd-breadcrumb a:not([data-gp-home-garden-parent])';
  const localURL = (root, value) => {
    const url = new URL(value, location.href);
    const base = new URL(root.dataset.collectionUrl, location.origin).pathname.replace(/\/$/, '');
    const state = stateFor(root);
    root.querySelectorAll(typeLinks).forEach(link => {
      const target = new URL(link.href, location.href);
      if (target.origin === location.origin && /\/collections\/[^/]+\/?$/.test(target.pathname)) state.paths.add(target.pathname);
    });
    return url.origin === location.origin && (url.pathname === base || state.paths.has(url.pathname)) ? url : null;
  };
  const ready = root => root?.dataset.sectionId && root.querySelector('[data-gp-pd-results]');
  function stateFor(root) {
    if (!states.has(root)) states.set(root, { sequence: 0, paths: new Set([new URL(root.dataset.collectionUrl, location.origin).pathname]), cache: new Map([[key(new URL(location.href)), { html: root.outerHTML, at: Date.now(), title: document.title, canonical: document.querySelector('link[rel=canonical]')?.href }]]) });
    return states.get(root);
  }
  async function navigate(root, url, { historyMode = 'push', focus, pagination = false } = {}) {
    const state = stateFor(root), sequence = ++state.sequence;
    state.controller?.abort();
    const controller = new AbortController();
    state.controller = controller;
    const status = root.querySelector('[data-gp-pd-status]');
    const zh = document.documentElement.lang.startsWith('zh');
    root.querySelector('[data-gp-pd-results]').setAttribute('aria-busy', 'true');
    status.textContent = zh ? '正在更新商品…' : 'Updating products…';
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const cached = state.cache.get(key(url));
      const crossCollection = url.pathname !== new URL(root.dataset.collectionUrl, location.origin).pathname;
      let html;
      if (cached && Date.now() - cached.at < 300000) html = cached.html;
      else {
        const endpoint = new URL(url);
        if (!crossCollection) endpoint.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetch(endpoint.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Section request failed');
        html = await response.text();
      }
      if (sequence !== state.sequence) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('.gp-pd');
      const results = next?.querySelector('[data-gp-pd-results]');
      if (!results || new URL(next.dataset.collectionUrl, location.origin).pathname !== url.pathname || html.includes('Liquid error')) throw new Error('Invalid section');
      const title = cached?.title ?? parsed.querySelector('title')?.textContent ?? document.title;
      const canonical = cached?.canonical ?? parsed.querySelector('link[rel=canonical]')?.href;
      state.cache.set(key(url), { html, at: Date.now(), title, canonical });
      if (crossCollection) {
        ['.gp-pd-hero', '.gp-pd-browse', '.gp-pd-guide'].forEach(selector => {
          const old = root.querySelector(selector), fresh = next.querySelector(selector);
          if (old && fresh) old.replaceWith(fresh);
        });
        document.title = title;
        if (canonical && document.querySelector('link[rel=canonical]')) document.querySelector('link[rel=canonical]').href = canonical;
      }
      ['handle', 'collectionUrl', 'sectionId'].forEach(name => { root.dataset[name] = next.dataset[name]; });
      state.paths.add(url.pathname);
      if (state.cache.size > 12) state.cache.delete(state.cache.keys().next().value);
      root.querySelector('[data-gp-pd-results]').replaceWith(results);
      const oldNav = root.querySelector('.gp-pd-nav'), newNav = next.querySelector('.gp-pd-nav');
      if (oldNav && newNav) {
        const oldLinks = [...oldNav.querySelectorAll('a')], newLinks = [...newNav.querySelectorAll('a')];
        if (oldLinks.length === newLinks.length) {
          oldLinks.forEach((link, index) => {
            const fresh = newLinks[index];
            link.href = fresh.href;
            link.className = fresh.className;
            if (fresh.hasAttribute('aria-current')) link.setAttribute('aria-current', fresh.getAttribute('aria-current'));
            else link.removeAttribute('aria-current');
          });
        } else oldNav.replaceWith(newNav);
      }
      if (historyMode === 'push' && key(url) !== key(new URL(location.href))) history.pushState(history.state, '', url.href);
      document.dispatchEvent(new CustomEvent('gp:planter-updated'));
      status.textContent = (zh ? '已更新：' : 'Updated: ') + (root.querySelector('.gp-pd-toolbar h2 span')?.textContent.trim() || '');
      if (focus?.type === 'select') root.querySelectorAll('[data-gp-pd-navigate], [data-gp-pd-sort]')[focus.index]?.focus({ preventScroll: true });
      if (focus?.type === 'link') [...root.querySelectorAll('.gp-pd-nav a')].find(a => a.href === focus.href)?.focus({ preventScroll: true });
      if (pagination) {
        const toolbar = root.querySelector('.gp-pd-toolbar');
        toolbar.setAttribute('tabindex', '-1');
        toolbar.focus({ preventScroll: true });
        toolbar.scrollIntoView({ block: 'start', behavior: 'instant' });
      }
    } catch (error) {
      if (sequence !== state.sequence) return;
      status.textContent = zh ? '暫時未能更新，請再試或' : 'Unable to update. Try again or ';
      const link = document.createElement('a');
      link.href = url.href;
      link.textContent = zh ? '開啟篩選結果' : 'open the filtered results';
      status.append(link);
      root.querySelectorAll('[data-gp-pd-navigate], [data-gp-pd-sort]').forEach(select => {
        select.value = [...select.options].find(option => option.defaultSelected)?.value || select.options[0].value;
      });
    } finally {
      clearTimeout(timer);
      if (sequence === state.sequence) root.querySelector('[data-gp-pd-results]').removeAttribute('aria-busy');
    }
  }
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest(typeLinks + ', .gp-pd-material a, .gp-pd-pages a, .gp-pd-grid > p a');
    const root = link?.closest('.gp-pd');
    if (!ready(root) || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = localURL(root, link.href);
    if (!url) return;
    event.preventDefault();
    navigate(root, url, { focus: { type: 'link', href: link.href }, pagination: !!link.closest('.gp-pd-pages') });
  });
  document.addEventListener('change', event => {
    const select = event.target, root = select.closest('.gp-pd');
    if (!root || !select.matches('[data-gp-pd-navigate], [data-gp-pd-sort]')) return;
    if (!ready(root)) {
      if (select.matches('[data-gp-pd-navigate]')) location.assign(select.value);
      else select.form.requestSubmit();
      return;
    }
    let url;
    if (select.matches('[data-gp-pd-navigate]')) url = localURL(root, select.value);
    else {
      url = new URL(location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
    }
    if (url) navigate(root, url, { focus: { type: 'select', index: [...root.querySelectorAll('[data-gp-pd-navigate], [data-gp-pd-sort]')].indexOf(select) } });
  });
  document.addEventListener('submit', event => {
    const form = event.target.closest('.gp-pd-sort-form'), root = form?.closest('.gp-pd');
    if (!ready(root)) return;
    event.preventDefault();
    const url = new URL(location.href);
    url.searchParams.set('sort_by', form.querySelector('[data-gp-pd-sort]').value);
    url.searchParams.delete('page');
    navigate(root, url);
  });
  window.addEventListener('popstate', () => {
    document.querySelectorAll('.gp-pd').forEach(root => {
      if (!ready(root)) return;
      const url = localURL(root, location.href);
      if (url) navigate(root, url, { historyMode: 'none' });
    });
  });
})();
